import React, { useCallback, useEffect } from 'react'
import { bool, func, shape } from 'prop-types'
import { FullPageOverlay, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import PersonHeader from './PersonDialogBox/PersonHeader/PersonHeader'
import PersonTabs from './PersonDialogBox/PersonTabs/PersonTabs'
import { PersonType } from '../../propTypes/personDataProps'
import useTreeData from '../../hooks/useTreeData'
import { usePersonViewerDispatch, ACTIONS } from './state/PersonViewerStore'

export default function PersonViewer({
  isAccessible = false,
  personData,
  clearPerson,
  tree,
  modal = true,
  overlay: overlayProp,
  onClose,
  allowMemoryUpload = true,
}) {
  const { t } = useTranslation()
  const internalOverlay = useOverlay({ initiallyOpen: modal })
  const overlay = overlayProp ?? internalOverlay
  const personViewerDispatch = usePersonViewerDispatch()
  const [personTreeData] = useTreeData(personData)
  const isFamousRelativesApp = !!window.location.pathname.match('famousrelatives')

  useEffect(() => {
    if (!allowMemoryUpload) {
      personViewerDispatch({
        type: ACTIONS.SET_LOCK_MEMORIES,
        lockMemories: true,
      })
    } else if (personTreeData.complete) {
      const personInfo = personTreeData?.res?.persons?.[0]?.personInfo?.[0]
      const isLocked = personInfo?.readOnly || personInfo?.canUserEdit === false || isFamousRelativesApp
      personViewerDispatch({
        type: ACTIONS.SET_LOCK_MEMORIES,
        lockMemories: isLocked,
      })
    }
  }, [personTreeData, personViewerDispatch, isFamousRelativesApp, allowMemoryUpload])

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      overlay.close()
    }
  }, [overlay, onClose])

  const handleTransitioned = useCallback(() => {
    overlay.handleTransitioned()
    if (!overlay.isOpen) {
      clearPerson?.()
    }
  }, [overlay, clearPerson])

  const personViewerContent = (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        height: modal ? '100%' : 'auto',
        minHeight: 0,
        overflow: modal ? 'hidden' : 'visible',
      }}
    >
      <PersonHeader personData={personData} isAccessible={isAccessible} />
      <PersonTabs personData={personData} tree={tree} isAccessible={isAccessible} />
    </div>
  )

  return modal ? (
    <FullPageOverlay
      isOpen={overlay.isOpen}
      transitioning={overlay.transitioning}
      handleTransitioned={handleTransitioned}
      close={handleClose}
      onClose={handleClose}
      title={t('common-ui:back.action', 'Back')}
      showClose
    >
      {personViewerContent}
    </FullPageOverlay>
  ) : (
    personViewerContent
  )
}

PersonViewer.propTypes = {
  clearPerson: func,
  isAccessible: bool,
  personData: PersonType,
  modal: bool,
  tree: shape({}),
  overlay: shape({}),
  onClose: func,
  allowMemoryUpload: bool,
}
